import {
  FETCH_SLIDES_START,
  FETCH_SLIDES_SUCCESS,
  FETCH_SLIDES_FAIL,
  FETCH_SLIDE_START,
  FETCH_SLIDE_SUCCESS,
  FETCH_SLIDE_FAIL,
  CREATE_SLIDE_START,
  CREATE_SLIDE_SUCCESS,
  CREATE_SLIDE_FAIL,
  UPDATE_SLIDE_START,
  UPDATE_SLIDE_SUCCESS,
  UPDATE_SLIDE_FAIL,
  DELETE_SLIDE_START,
  DELETE_SLIDE_SUCCESS,
  DELETE_SLIDE_FAIL,
  SET_SLIDE_VISIBILITY_SUCCESS,
  REORDER_SLIDES_LOCAL,
} from './action-types';

const initialState = {
  slides: [],
  slide: null,
  isLoading: false,
  error: null,
};

const slidesReducer = (state = initialState, { type, payload } = {}) => {
  switch (type) {
  case FETCH_SLIDES_START:
    return { ...state, isLoading: true, error: null };

  case FETCH_SLIDES_SUCCESS:
    // Сбрасываем `localOrder` после refetch — серверный `position` теперь
    // авторитетен, оптимистичный порядок больше не нужен.
    return {
      ...state,
      slides: Array.isArray(payload)
        ? payload.map((s) => {
          const { localOrder: _ignored, ...rest } = s || {};
          return rest;
        })
        : [],
      isLoading: false,
    };

  case FETCH_SLIDES_FAIL:
    return { ...state, isLoading: false, error: payload };

  case FETCH_SLIDE_START:
    return { ...state, slide: null, isLoading: true, error: null };

  case FETCH_SLIDE_SUCCESS:
    return { ...state, slide: payload, isLoading: false };

  case FETCH_SLIDE_FAIL:
    return { ...state, isLoading: false, error: payload };

  case CREATE_SLIDE_START:
    return { ...state, isLoading: true, error: null };

  case CREATE_SLIDE_SUCCESS:
    return {
      ...state,
      slides: [...state.slides, payload],
      isLoading: false,
    };

  case CREATE_SLIDE_FAIL:
    return { ...state, isLoading: false, error: payload };

  case UPDATE_SLIDE_START:
    return { ...state, isLoading: true, error: null };

  case UPDATE_SLIDE_SUCCESS:
    return {
      ...state,
      slides: state.slides.map((s) => (s.id === payload?.id ? { ...s, ...payload } : s)),
      slide: state.slide?.id === payload?.id ? { ...state.slide, ...payload } : state.slide,
      isLoading: false,
    };

  case UPDATE_SLIDE_FAIL:
    return { ...state, isLoading: false, error: payload };

  case DELETE_SLIDE_START:
    return { ...state, isLoading: true, error: null };

  case DELETE_SLIDE_SUCCESS:
    return {
      ...state,
      // Cascade удаление: убираем сам слайд и всех потомков по дереву.
      slides: removeWithDescendants(state.slides, payload),
      isLoading: false,
    };

  case DELETE_SLIDE_FAIL:
    return { ...state, isLoading: false, error: payload };

  case SET_SLIDE_VISIBILITY_SUCCESS:
    return {
      ...state,
      slides: state.slides.map((s) =>
        (s.id === payload.id ? { ...s, isVisible: payload.isVisible } : s)),
    };

  case REORDER_SLIDES_LOCAL: {
    // Backend хранит `position` как lexorank-строку. Здесь мы НЕ затираем
    // `position` числом (это нарушает контракт типа и ломает сортировку
    // в селекторе), а кладём отдельное поле `localOrder` (number) только
    // для оптимистичной сортировки до прихода ответа /reorder.
    // selectSlideTree даёт `localOrder` приоритет над `position` при
    // сортировке детей одного родителя.
    const positionById = new Map();
    (Array.isArray(payload) ? payload : []).forEach((it) => {
      positionById.set(it.id, { parentId: it.parentId ?? null, localOrder: it.orderIndex });
    });
    return {
      ...state,
      slides: state.slides.map((s) => {
        const next = positionById.get(s.id);
        return next ? { ...s, parentId: next.parentId, localOrder: next.localOrder } : s;
      }),
    };
  }

  default:
    return state;
  }
};

/**
 * Удалить слайд `rootId` и всех его потомков из flat-списка.
 * Обходит дерево по `parentId` поэтапно, поэтому работает на любой глубине.
 */
function removeWithDescendants(slides, rootId) {
  const toDrop = new Set([rootId]);
  let addedAny = true;
  while (addedAny) {
    addedAny = false;
    for (let i = 0; i < slides.length; i += 1) {
      const s = slides[i];
      if (!toDrop.has(s.id) && s.parentId != null && toDrop.has(s.parentId)) {
        toDrop.add(s.id);
        addedAny = true;
      }
    }
  }
  return slides.filter((s) => !toDrop.has(s.id));
}

export default slidesReducer;
